"use client";

import { useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { ArrowRight, Mic } from "lucide-react";
import { Link } from "@/i18n/navigation";
import posthog from "posthog-js";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { createClient } from "@/lib/supabase/client";

const DEFAULT_DELAY_MS = 15_000;
const SHOWN_KEY = "blog:signupPromptShown";

interface BlogSignupPromptProps {
  delayMs?: number;
}

export function BlogSignupPrompt({ delayMs = DEFAULT_DELAY_MS }: BlogSignupPromptProps) {
  const t = useTranslations("blog");
  const [open, setOpen] = useState(false);
  const [signedIn, setSignedIn] = useState<boolean | null>(null);
  const scheduledRef = useRef<number | null>(null);

  useEffect(() => {
    const supabase = createClient();
    supabase.auth.getUser().then(({ data: { user } }) => {
      setSignedIn(Boolean(user));
    });
  }, []);

  useEffect(() => {
    if (signedIn !== false) return;

    let alreadyShown = false;
    try {
      alreadyShown = window.sessionStorage.getItem(SHOWN_KEY) === "1";
    } catch {
      // sessionStorage may be unavailable — treat as not shown.
    }
    if (alreadyShown) return;

    const timer = window.setTimeout(() => {
      setOpen(true);
      try {
        window.sessionStorage.setItem(SHOWN_KEY, "1");
      } catch {
        // Ignore storage write errors.
      }
      posthog.capture("blog_signup_prompt_shown");
    }, delayMs);
    scheduledRef.current = timer;

    return () => {
      window.clearTimeout(timer);
    };
  }, [signedIn, delayMs]);

  if (signedIn !== false) return null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="text-center sm:text-center">
        <DialogHeader className="items-center sm:items-center">
          <div className="mx-auto mb-1 flex size-10 items-center justify-center rounded-full bg-accent/10 text-accent">
            <Mic className="size-5" />
          </div>
          <DialogTitle className="text-xl">{t("signupPromptTitle")}</DialogTitle>
          <DialogDescription>{t("signupPromptDescription")}</DialogDescription>
        </DialogHeader>
        <Button
          asChild
          size="lg"
          className="mt-2 w-full bg-accent text-white font-bold hover:bg-accent/90"
        >
          <Link href="/login" onClick={() => setOpen(false)}>
            {t("signupPromptCta")}
            <ArrowRight className="size-4" />
          </Link>
        </Button>
      </DialogContent>
    </Dialog>
  );
}
